import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({
    payment_date: dayjs().format('YYYY-MM-DD'),
    type: 'client',
    client_id: '',
    supplier_id: '',
    amount: '',
    notes: '',
  });

  useEffect(() => { fetchPayments(); fetchDropdowns(); }, [filter]);

  const fetchPayments = async () => {
    try {
      const params = filter !== 'all' ? { type: filter } : {};
      const response = await api.get('/payments', { params });
      setPayments(response.data.data);
    } catch (error) {
      toast.error('Gabim');
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [c, s] = await Promise.all([api.get('/clients'), api.get('/suppliers')]);
      setClients(c.data);
      setSuppliers(s.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/payments', form);
      toast.success('Pagesa u regjistrua!');
      setShowModal(false);
      setForm({ payment_date: dayjs().format('YYYY-MM-DD'), type: 'client', client_id: '', supplier_id: '', amount: '', notes: '' });
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Jeni i sigurt?')) return;
    try {
      await api.delete(`/payments/${id}`);
      toast.success('U fshi!');
      fetchPayments();
    } catch (error) {
      toast.error('Gabim');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Pagesat</h1>
        <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
          <PlusIcon className="w-5 h-5" /> Shto Pagesë
        </button>
      </div>

      <div className="card">
        <div className="flex gap-2 mb-4">
          <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-lg ${filter === 'all' ? 'bg-primary-600 text-white' : 'bg-gray-100'}`}>Të gjitha</button>
          <button onClick={() => setFilter('client')} className={`px-4 py-2 rounded-lg ${filter === 'client' ? 'bg-primary-600 text-white' : 'bg-gray-100'}`}>Nga Klientë</button>
          <button onClick={() => setFilter('supplier')} className={`px-4 py-2 rounded-lg ${filter === 'supplier' ? 'bg-primary-600 text-white' : 'bg-gray-100'}`}>Tek Furnitorë</button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Data</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Lloji</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Klient/Furnitor</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Shënim</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Shuma</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Veprime</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-2">{dayjs(p.payment_date).format('DD/MM/YY')}</td>
                    <td className="py-3 px-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${p.type === 'client' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {p.type === 'client' ? 'Klient' : 'Furnitor'}
                      </span>
                    </td>
                    <td className="py-3 px-2">{p.client?.name || p.supplier?.name}</td>
                    <td className="py-3 px-2 text-gray-500">{p.notes}</td>
                    <td className={`py-3 px-2 text-right font-medium ${p.type === 'client' ? 'text-green-600' : 'text-red-600'}`}>€{p.amount}</td>
                    <td className="py-3 px-2 text-right">
                      <button onClick={() => handleDelete(p.id)} className="p-1 text-gray-500 hover:text-red-600"><TrashIcon className="w-5 h-5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Shto Pagesë</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                <input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="input-field" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lloji</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, client_id: '', supplier_id: '' })} className="input-field">
                  <option value="client">Nga Klienti</option>
                  <option value="supplier">Tek Furnitori</option>
                </select>
              </div>
              {form.type === 'client' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Klienti</label>
                  <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="input-field" required>
                    <option value="">-- Zgjidh --</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name} (€{c.balance?.toFixed(2)})</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Furnitori</label>
                  <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className="input-field" required>
                    <option value="">-- Zgjidh --</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name} (€{s.balance?.toFixed(2)})</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shuma (€)</label>
                <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-field" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shënim</label>
                <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-field" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Anulo</button>
                <button type="submit" className="btn-primary flex-1">Ruaj</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
